import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Download, File, Image, Paperclip, X } from 'lucide-react';
import backendApi from '../../services/backendApi';
import '../../styles/ticketAttachmentViewer.css';

const previewableType = type => /^(image\/(png|jpeg|gif|webp)|application\/pdf|video\/(mp4|webm|ogg|quicktime))$/.test(type || '');

const formatSize = size => {
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function TicketAttachments({ ticketId, compact = false }) {
  const panelId = useId();
  const [expanded, setExpanded] = useState(!compact);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    let active = true;
    setFiles([]);
    setLoading(true);
    setError('');
    setPreview(null);
    setExpanded(!compact);
    backendApi.getTicketAttachments(ticketId)
      .then(data => { if (active) setFiles(data.attachments || []); })
      .catch(() => { if (active) setError('Impossible de charger les pièces jointes.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [compact, ticketId]);

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  useEffect(() => {
    if (!preview) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const onKeyDown = event => {
      if (event.key === 'Escape') setPreview(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [preview]);

  const openFile = async file => {
    setOpeningId(file.id);
    setError('');
    try {
      const data = await backendApi.getTicketAttachment(ticketId, file.id);
      const bytes = Uint8Array.from(atob(data.data), character => character.charCodeAt(0));
      const type = previewableType(data.type) ? data.type : 'application/octet-stream';
      const url = URL.createObjectURL(new Blob([bytes], { type }));
      setPreview({ name: data.name, type, url });
    } catch {
      setError('Impossible d’ouvrir cette pièce jointe.');
    } finally {
      setOpeningId(null);
    }
  };

  return <><section className={compact ? 'story-attachments story-attachments--compact' : 'story-attachments mt-8'}>
    <button type="button" aria-expanded={expanded} aria-controls={panelId} onClick={() => setExpanded(value => !value)}
      className="story-attachments__trigger inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500">
      <Paperclip size={16} aria-hidden="true" />
      <span>Pièces jointes {compact ? '· ' : '('}{loading ? '…' : error && !files.length ? '—' : files.length}{compact ? '' : ')'}</span>
      {!compact && <ChevronDown size={16} aria-hidden="true" className={expanded ? 'rotate-180' : ''} />}
    </button>

    {error && <p role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    <div id={panelId} hidden={!expanded} className="story-attachments__panel max-h-[45vh] overflow-y-auto pr-2">
    {loading && <p className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">Chargement…</p>}
    {!loading && !error && files.length === 0 && <p className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">Aucune pièce jointe</p>}

    {files.length > 0 && <ul className={compact ? 'grid gap-2' : 'grid gap-3 sm:grid-cols-2'}>
      {files.map(file => <li key={file.id}>
        <button type="button" onClick={() => openFile(file)} disabled={openingId === file.id}
          className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left transition-colors hover:border-purple-300 hover:bg-purple-50 disabled:opacity-60">
          {file.mimetype?.startsWith('image/')
            ? <Image className="h-5 w-5 flex-shrink-0 text-[#6B4C9A]" aria-hidden="true" />
            : <File className="h-5 w-5 flex-shrink-0 text-[#6B4C9A]" aria-hidden="true" />}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-gray-800">{file.name}</span>
            {formatSize(file.file_size) && <span className="block text-xs text-gray-500">{formatSize(file.file_size)}</span>}
          </span>
          <Download className="h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
          <span className="sr-only">{openingId === file.id ? 'Ouverture…' : `Ouvrir ${file.name}`}</span>
        </button>
      </li>)}
    </ul>}

    </div>
  </section>
    {preview && createPortal(<div className="ticket-attachment-viewer" role="dialog" aria-modal="true" aria-label={`Aperçu de ${preview.name}`}>
      <header className="ticket-attachment-viewer__header">
        <span className="ticket-attachment-viewer__name" title={preview.name}>{preview.name}</span>
        <div className="ticket-attachment-viewer__actions">
          <a href={preview.url} download={preview.name} aria-label={`Télécharger ${preview.name}`}><Download size={18} aria-hidden="true" />Télécharger</a>
          <button ref={closeButtonRef} type="button" aria-label="Fermer l’aperçu" onClick={() => setPreview(null)}><X size={22} aria-hidden="true" /></button>
        </div>
      </header>
      <div className="ticket-attachment-viewer__content">
        {preview.type.startsWith('image/') && <img src={preview.url} alt={preview.name} />}
        {preview.type === 'application/pdf' && <iframe title={preview.name} src={preview.url} />}
        {preview.type.startsWith('video/') && <video controls src={preview.url} aria-label={preview.name} />}
        {!previewableType(preview.type) && <p>Ce fichier ne peut pas être affiché ici. Utilisez « Télécharger » pour l’ouvrir.</p>}
      </div>
    </div>, document.body)}
  </>;
}
